import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { AssetCategory } from '../../sj-orm/models/asset.dto';
import {
  CommercialRealEstateDetails,
  CommercialRealEstateGeneral,
} from '../../sj-orm/models/assets/categories';
import { CommercialRealEstateType } from '../../sj-orm/models/assets/enums';
import { ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { makeDtoFilter } from './filters/makeDtoFilter';
import { baseAssetFields } from './fields';

export type CommercialRealEstate = CommercialRealEstateFormGeneralType &
  CommercialRealEstateFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const CommercialRealEstateList = [
  {
    key: CommercialRealEstateType.retail,
    label: 'forms_assets:commercialRealEstate.enums.retail',
  },
  {
    key: CommercialRealEstateType.office,
    label: 'forms_assets:commercialRealEstate.enums.office',
  },
  {
    key: CommercialRealEstateType.warehouse,
    label: 'forms_assets:commercialRealEstate.enums.warehouse',
  },
  {
    key: CommercialRealEstateType.production,
    label: 'forms_assets:commercialRealEstate.enums.production',
  },
  {
    key: CommercialRealEstateType.recreation,
    label: 'forms_assets:commercialRealEstate.enums.recreation',
  },
  {
    key: CommercialRealEstateType.agricultural,
    label: 'forms_assets:commercialRealEstate.enums.agricultural',
  },
  {
    key: CommercialRealEstateType.condominium,
    label: 'forms_assets:commercialRealEstate.enums.condominium',
  },
  {
    key: CommercialRealEstateType.other,
    label: 'forms_assets:commercialRealEstate.enums.other',
  },
];

type CommercialRealEstateHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  otherType: QuestionType.TEXT;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
};

type CommercialRealEstateHelpDetails = {
  commercialRealEstateNumberName?: QuestionType.TEXT;
  commercialRealEstateNumber?: QuestionType.TEXT;
  constructionDate?: QuestionType.DATE_TIME;
  apartmentSquare?: QuestionType.NUMBER;
  compositionOfObjects?: QuestionType.TEXT;
  landPlot?: QuestionType.CHECKBOX;
  chooseLandPlot?: QuestionType.PICK_ARRAY_OF_DTO;
  address?: QuestionType.ADDRESS;
  contacts?: QuestionType.PICK_ARRAY_OF_DTO;
  addressComment?: QuestionType.TEXT_MULTILINE;
  attachments?: QuestionType.FILE_UPLOAD;
};

type CommercialRealEstateFormGeneralType = TDtoWrapper<
  CommercialRealEstateGeneral,
  CommercialRealEstateHelpGeneral
>;
type CommercialRealEstateFormDetailsType = TDtoWrapper<
  CommercialRealEstateDetails,
  CommercialRealEstateHelpDetails
>;

const CommercialRealEstateFormGeneral: CommercialRealEstateFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:commercialRealEstate.inputs.type.label',
    value: CommercialRealEstateType.retail,
    items: CommercialRealEstateList,
    required: true,
  },
  otherType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commercialRealEstate.inputs.otherType.label',
    showIf: {
      type: {
        value: CommercialRealEstateType.other,
      },
    },
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:commercialRealEstate.inputs.currentMarketPrice.label',
    tooltip: 'forms_assets:commercialRealEstate.inputs.currentMarketPrice.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
};

const CommercialRealEstateFormDetails: CommercialRealEstateFormDetailsType = {
  commercialRealEstateNumberLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:common.inputs.realEstateNumberLabel.label',
    order: 1,
  },
  commercialRealEstateNumberName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commercialRealEstate.inputs.commercialRealEstateNumberName.label',
    order: 2,
  },
  commercialRealEstateNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commercialRealEstate.inputs.commercialRealEstateNumber.label',
    order: 3,
  },
  commencementDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:commercialRealEstate.inputs.commencementDate.label',
    tooltip: 'forms_assets:commercialRealEstate.inputs.commencementDate.tooltip',
    order: 4,
  },
  areaOfTheApartments: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:commercialRealEstate.inputs.areaOfTheApartments.label',
    order: 5,
  },
  compositionOfObjects: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commercialRealEstate.inputs.compositionOfObjects.label',
    order: 6,
  },
  landPlot: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:commercialRealEstate.inputs.landPlot.label',
    order: 7,
  },
  chooseLandPlot: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:commercialRealEstate.inputs.chooseLandPlot.label',
    items: CollectionName.ASSETS,
    showIf: {
      landPlot: {
        value: true,
      },
    },
    makeExtFilter: makeDtoFilter({
      categories: [AssetCategory.LAND],
      withCountryFilter: true,
    }),
    deps: ['country'],
    dtoType: AssetCategory.LAND,
    order: 8,
  },
  contacts: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.contacts.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    dtoType: [
      ContactType.INDIVIDUAL,
      ContactType.LEGAL_ENTITY,
      ContactType.BROKER,
      ContactType.BANK,
    ],
    order: 9,
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    value: undefined,
    colSpan: 2,
    order: 9,
  },
  addressComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.addressComment.label',
    value: '',
    order: 11,
  },
  attachments: baseAssetFields.attachments,
};

const CommercialRealEstateForm = [
  CommercialRealEstateFormGeneral,
  CommercialRealEstateFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { CommercialRealEstateForm };
